import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FolderOpen, Trash2, Warehouse, ShoppingCart, ShieldCheck, LogOut, User as UserIcon } from "lucide-react";
import { BRAND } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { listProjects, createProject, deleteProject } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const EMPTY_GFX =
  "https://static.prod-images.emergentagent.com/jobs/e21259f6-21b2-4502-ada0-8c35ebc42d51/images/b1bcc8d9ccf78e715c55a57299f92b264a0e3180c3d9947d873e50aad6dcf3b8.png";

export default function Dashboard() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    client_name: "",
    location: "",
    project_type: "warehouse",
    units: "mm",
    ceiling_height: 6000,
  });

  useEffect(() => {
    (async () => {
      try {
        const p = await listProjects();
        setProjects(p);
      } catch (e) {
        console.error(e);
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  async function handleCreate() {
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      const p = await createProject({
        ...form,
        ceiling_height: Number(form.ceiling_height) || 6000,
      });
      setOpen(false);
      toast.success("Project created");
      nav(`/editor/${p.id}`);
    } catch (e) {
      toast.error("Could not create project");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 px-6 lg:px-10 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs font-mono">/ projects</span>
        </div>
        <div className="flex items-center gap-1.5">
          {user?.role === "admin" && (
            <Button
              data-testid="link-admin"
              size="sm"
              variant="outline"
              className="rounded-sm h-8 font-mono text-[10px] uppercase tracking-widest"
              onClick={() => nav("/admin")}
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Admin
            </Button>
          )}
          <div className="hidden sm:flex items-center gap-1.5 h-8 px-2 rounded-sm border border-border/60 bg-background/40">
            <UserIcon className="w-3 h-3 text-muted-foreground" />
            <span data-testid="dash-user-email" className="font-mono text-[10px] tracking-wider text-muted-foreground max-w-[140px] truncate">{user?.email}</span>
            <span className={`px-1.5 py-0.5 rounded-sm font-mono text-[9px] uppercase tracking-widest ${
              user?.role === "admin" ? "bg-primary/15 text-primary" : "bg-secondary/60 text-muted-foreground"
            }`}>
              {user?.role}
            </span>
          </div>
          <Button
            data-testid="dash-logout"
            size="sm"
            variant="ghost"
            className="rounded-sm h-8 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
            onClick={logout}
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary mb-2">// Workspace</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Your projects</h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              {projects.length} {projects.length === 1 ? "project" : "projects"}
            </p>
          </div>
          <Button
            data-testid="new-project-btn"
            onClick={() => setOpen(true)}
            className="text-white hover:brightness-110 rounded-sm h-10"
            style={{ background: BRAND.red }}
          >
            <Plus className="w-4 h-4 mr-1.5" /> New project
          </Button>
        </div>

        {busy ? (
          <div className="text-muted-foreground text-sm font-mono">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div data-testid="empty-state" className="border border-dashed border-border/60 rounded-sm p-12 flex flex-col items-center text-center bg-card/40">
            <img src={EMPTY_GFX} alt="" className="w-44 h-44 object-contain opacity-80 mb-5" />
            <h3 className="text-xl font-semibold tracking-tight">No projects yet</h3>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
              Spin up your first warehouse layout. Pick a floor shape, drop racks, render in 3D.
            </p>
            <Button
              data-testid="empty-create-btn"
              onClick={() => setOpen(true)}
              className="mt-6 text-white hover:brightness-110 rounded-sm"
              style={{ background: BRAND.red }}
            >
              <Plus className="w-4 h-4 mr-1.5" /> Create project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: i * 0.03 }}
                className="border border-border/60 bg-card hover:border-primary/60 transition-colors group cursor-pointer"
                onClick={() => nav(`/editor/${p.id}`)}
                data-testid={`project-card-${p.id}`}
              >
                <div className="aspect-[16/10] bg-background/60 grid-bg-fine relative overflow-hidden">
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center opacity-40">
                      <FolderOpen className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold tracking-tight truncate">{p.name}</h3>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                        {p.client_name || "—"}
                      </div>
                    </div>
                    <button
                      data-testid={`delete-project-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-3">
                    <span className={p.project_type === "supermarket" ? "text-emerald-500" : "text-primary"}>
                      {p.project_type === "supermarket" ? "SUPERMARKET" : "WAREHOUSE"}
                    </span>
                    <span>·</span>
                    <span>{p.units}</span>
                    <span>·</span>
                    <span>{p.objects?.length || 0} objects</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="tracking-tight">New project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3.5">
            {/* Project type — warehouse vs supermarket */}
            <div className="space-y-1.5">
              <Label>Module</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "warehouse", label: "Warehouse", icon: Warehouse, hint: "Racks · Mezzanine · MHE" },
                  { id: "supermarket", label: "Supermarket", icon: ShoppingCart, hint: "Gondolas · Freezers · POS" },
                ].map((opt) => {
                  const active = form.project_type === opt.id;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`np-type-${opt.id}`}
                      onClick={() => setForm((s) => ({ ...s, project_type: opt.id }))}
                      className={`border rounded-sm px-3 py-3 text-left transition-colors ${
                        active
                          ? "border-primary bg-primary/10"
                          : "border-border/60 hover:border-border bg-background/40"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} strokeWidth={2} />
                        <span className={`text-sm font-semibold tracking-tight ${active ? "text-foreground" : ""}`}>
                          {opt.label}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground mt-1">
                        {opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="np-name">Project name</Label>
              <Input
                id="np-name"
                data-testid="np-name-input"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Acme Logistics — DC2"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np-client">Client</Label>
              <Input
                id="np-client"
                data-testid="np-client-input"
                value={form.client_name}
                onChange={(e) => setForm((s) => ({ ...s, client_name: e.target.value }))}
                placeholder="Acme Logistics Pvt Ltd"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np-location">Location</Label>
              <Input
                id="np-location"
                data-testid="np-location-input"
                value={form.location}
                onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
                placeholder="Bhiwandi, MH"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Units</Label>
                <Select
                  value={form.units}
                  onValueChange={(v) => setForm((s) => ({ ...s, units: v }))}
                >
                  <SelectTrigger data-testid="np-units-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mm">mm</SelectItem>
                    <SelectItem value="m">m</SelectItem>
                    <SelectItem value="ft">ft</SelectItem>
                    <SelectItem value="in">in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="np-h">Ceiling (mm)</Label>
                <Input
                  id="np-h"
                  data-testid="np-ceiling-input"
                  type="number"
                  value={form.ceiling_height}
                  onChange={(e) => setForm((s) => ({ ...s, ceiling_height: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="np-create-btn" onClick={handleCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Create & open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
